/* eslint-disable react-hooks/rules-of-hooks */
'use client'

import React, { JSX, useEffect, useRef, useState } from 'react'
import { Box, Flex, Image, Text } from '@chakra-ui/react'

import { ImageType } from 'appTypes/items'
import { handleProductImageFallback } from 'utils/imageFallback'
import { useSlider } from 'utils/useSlider'

import { ProductImageWrapper, Slide, SlidePreview, SliderBullet, StyledSlider } from './styles'
import { useTranslation } from 'react-i18next'
import { getBackgroundImageUrl } from 'app/utils/helpers/helpers'

type Props = {
  images: ImageType[] | null | undefined
}

const ProductImagesSlider = ({ images }: Props): JSX.Element | null => {
  const { t } = useTranslation('common')
  if (!images) {
    return null
  }
  const [isVisible, setIsVisible] = useState(false)

  const ref = useRef<HTMLDivElement>(null)

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { touchHandlers, selectedSlide, handleSlideSelect } = useSlider({
    slides: images,
  })

  const [currentMediumUrl, setCurrentMediumUrl] = useState<string | undefined>(images[0].mediumUrl)
  const [currentThumbnailUrl, setCurrentThumbnailUrl] = useState<string | undefined>(images[0].miniUrl)

  const [mediumErrorLevel, setMediumErrorLevel] = useState(0)
  const [thumbnailErrorLevel, setThumbnailErrorLevel] = useState(0)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      { threshold: 0.1 },
    )

    if (ref.current) observer.observe(ref.current)

    return () => observer.disconnect()
  }, [])

  const handleMediumImageErrorFallback = (index: number) => {
    handleProductImageFallback(
      images[index],
      currentMediumUrl,
      mediumErrorLevel,
      setCurrentMediumUrl,
      setMediumErrorLevel,
    )
  }

  const handleThumbnailImageErrorFallback = (index: number) => {
    handleProductImageFallback(
      images[index],
      currentThumbnailUrl,
      thumbnailErrorLevel,
      setCurrentThumbnailUrl,
      setThumbnailErrorLevel,
    )
  }

  return (
    <Box>
      <ProductImageWrapper>
        <StyledSlider {...touchHandlers}>
          {images.map((image, index: number) => (
            <Slide
              key={index}
              isActive={selectedSlide === index}
              isPrevious={selectedSlide - 1 >= index}
              isNext={selectedSlide + 1 <= index}
            >
              <Box
                ref={ref}
                width="100%"
                height="100%"
                backgroundImage={isVisible && currentMediumUrl ? getBackgroundImageUrl(currentMediumUrl) : 'none'}
                backgroundSize="cover"
                backgroundPosition="center"
                backgroundRepeat="no-repeat"
                onError={() => handleMediumImageErrorFallback(index)}
              >
                {!isVisible && (
                  <Text
                    fontSize="sm"
                    color="gray.400"
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                  >
                    {t('loading')}
                  </Text>
                )}
              </Box>
            </Slide>
          ))}

          {images.length > 1 && (
            <Flex
              position="absolute"
              bottom="12px"
              left="50%"
              transform="translateX(-50%)"
              display={{ base: 'flex', lg: 'none' }}
            >
              {images.map((_, index: number) => (
                <SliderBullet key={index} isActive={selectedSlide === index} onClick={() => handleSlideSelect(index)} />
              ))}
            </Flex>
          )}
        </StyledSlider>
      </ProductImageWrapper>
      {images.length > 1 && (
        <Box
          display={{ base: 'none', lg: 'grid' }}
          gridTemplateColumns="repeat(10, 1fr)"
          gridGap="10px"
          marginTop="12px"
        >
          {images.map((image, index: number) => (
            <SlidePreview
              key={index}
              onClick={() => handleSlideSelect(index)}
              style={{
                width: '40px',
                height: '40px',
                overflow: 'hidden',
              }}
            >
              <Image
                src={currentThumbnailUrl}
                alt={image.productName}
                onError={() => {
                  handleThumbnailImageErrorFallback(index)
                }}
                width="100%"
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                }}
              />
            </SlidePreview>
          ))}
        </Box>
      )}
    </Box>
  )
}

export default ProductImagesSlider
